import React, { useState } from "react";

import ocLogo from "../../../assets/images/home/01-hero/oc-logo.svg";
import {
  MENU_EDU_CHAIN,
  HOME_PRODUCTS,
  MENU_DEVELOPER,
  MENU_FUNDING,
} from "../../../constants";
import "./HomeHeader.scss";

// "More" mega-dropdown — order: EDU Chain, Product, Developers, Funding, DAO
const MORE_COLUMNS = [
  [{ label: "EDU Chain", items: MENU_EDU_CHAIN }],
  [{ label: "Product", items: HOME_PRODUCTS }],
  [{ label: "Developers", items: MENU_DEVELOPER }],
  [{ label: "Funding", items: MENU_FUNDING }],
  [{ label: "DAO", href: "https://dao.opencampus.xyz" }],
];

// Primary homepage nav: three in-page scroll links + the "More" catch-all.
// Links use "/#anchor" so they also work from other pages (e.g. /oc-achievements):
// on the homepage they smooth-scroll, elsewhere they route home and then scroll.
const NAV = [
  { label: "Community", href: "/#community" }, // For people ready to do more with AI
  { label: "Tools", href: "/#tools" }, // Tools to support you on your learning journey
  { label: "About", href: "/#about" }, // Built on years of education impact
  { label: "More", more: MORE_COLUMNS },
];

// Handles any link that contains a "#anchor". If the target section exists on
// the current page, smooth-scroll to it; otherwise navigate to the homepage and
// let it scroll to the anchor on load. Plain links (no "#") fall through to the
// browser's default navigation.
const handleNavClick = (e, href, onDone) => {
  if (!href) return;
  const i = href.indexOf("#");
  if (i === -1) return;
  const id = href.slice(i); // "#community"
  const el = document.querySelector(id);
  e.preventDefault();
  if (onDone) onDone();
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  } else {
    window.location.assign(`/${id}`);
  }
};

const Chevron = () => (
  <svg
    className="home-header__chevron"
    width="11"
    height="6"
    viewBox="0 0 11 6"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M1 1l4.25 4L9.5 1"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export default function HomeHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  return (
    <header className="home-header">
      <div className="home-header__pill">
        <a className="home-header__brand" href="/">
          <img src={ocLogo} alt="Open Campus" />
        </a>

        <nav className="home-header__nav">
          {NAV.map((item) =>
            item.more ? (
              <div
                className="home-header__item home-header__item--menu"
                key={item.label}
              >
                <button type="button" className="home-header__link">
                  {item.label}
                  <Chevron />
                </button>
                <div className="home-header__dropdown home-header__dropdown--mega">
                  {item.more.map((column, ci) => (
                    <div className="home-header__mega-col" key={ci}>
                      {column.map((group) =>
                        group.items ? (
                          <div
                            className="home-header__mega-group"
                            key={group.label}
                          >
                            <span className="home-header__mega-title">
                              {group.label}
                            </span>
                            {group.items.map((sub) => (
                              <a
                                className="home-header__dropdown-link"
                                href={sub.url}
                                key={sub.title}
                                onClick={(e) => handleNavClick(e, sub.url)}
                              >
                                {sub.title}
                              </a>
                            ))}
                          </div>
                        ) : (
                          <div
                            className="home-header__mega-group"
                            key={group.label}
                          >
                            <a
                              className="home-header__dropdown-link home-header__dropdown-link--solo"
                              href={group.href}
                            >
                              {group.label}
                            </a>
                          </div>
                        )
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <a
                className="home-header__link"
                href={item.href}
                key={item.label}
                onClick={(e) => handleNavClick(e, item.href)}
              >
                {item.label}
              </a>
            )
          )}
        </nav>

        <button
          type="button"
          className={`home-header__burger${mobileOpen ? " is-open" : ""}`}
          aria-label="Toggle menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>

      {mobileOpen && (
        <div className="home-header__mobile">
          {NAV.map((item) =>
            item.more ? (
              <div className="home-header__mobile-group" key={item.label}>
                <button
                  type="button"
                  className="home-header__mobile-link home-header__mobile-link--toggle"
                  aria-expanded={moreOpen}
                  onClick={() => setMoreOpen((v) => !v)}
                >
                  {item.label}
                  <Chevron />
                </button>
                {moreOpen &&
                  item.more.flat().map((group) =>
                  group.items ? (
                    <div className="home-header__mobile-sub" key={group.label}>
                      <span className="home-header__mobile-sublabel">
                        {group.label}
                      </span>
                      {group.items.map((sub) => (
                        <a
                          className="home-header__mobile-sublink"
                          href={sub.url}
                          key={sub.title}
                          onClick={(e) =>
                            handleNavClick(e, sub.url, () => setMobileOpen(false))
                          }
                        >
                          {sub.title}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <a
                      className="home-header__mobile-sublink"
                      href={group.href}
                      key={group.label}
                    >
                      {group.label}
                    </a>
                  )
                )}
              </div>
            ) : (
              <div className="home-header__mobile-group" key={item.label}>
                <a
                  className="home-header__mobile-link"
                  href={item.href}
                  onClick={(e) =>
                    handleNavClick(e, item.href, () => setMobileOpen(false))
                  }
                >
                  {item.label}
                </a>
              </div>
            )
          )}
        </div>
      )}
    </header>
  );
}
